'use client'

import { useContext, useState } from 'react'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { clickEvent, selfServiceViewScreenEvent } from '@marketplace-web/complaints/complaints-data'

import { completeTransaction } from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { BrazeContext, brazeLogCustomEvent } from '@marketplace-web/braze/braze-feature'
import { BrazeCustomEvent } from '@marketplace-web/braze/braze-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'

import ConfirmDeliveredEverythingIsOkModal from './ConfirmDeliveredEverythingIsOkModal'

const CompleteDeliveredTransaction = ({
  primary,
  title,
  inverse,
  theme,
  size,
}: ConversationActionProps) => {
  const { track } = useTracking()

  const userExternalId = useContext(BrazeContext).userExternalId.value

  const { conversation } = useConversationContext()
  const transactionId = conversation?.transaction?.id

  const isBrazeEnabled = useFeatureSwitch('braze_sdk_enabled')
  const [isModalOpen, setIsModalOpen] = useState(false)

  function trackConfirmButtonClick() {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'confirm_everything_is_ok',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function trackModalScreen() {
    if (!transactionId) return

    track(
      selfServiceViewScreenEvent({
        screen: 'confirm_everything_is_ok_modal',
        transactionId,
      }),
    )
  }

  function openModal() {
    trackConfirmButtonClick()
    trackModalScreen()
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
  }

  async function confirmCompleteTransaction() {
    if (!transactionId) return

    const response = await completeTransaction(transactionId)

    if ('errors' in response) return

    if (isBrazeEnabled) {
      brazeLogCustomEvent(
        {
          event: BrazeCustomEvent.EscrowPurchaseCompleted,
          modelId: transactionId,
          userExternalId,
        },
        {
          defer: true,
        },
      )
    }

    reloadPage()
  }

  if (!transactionId) return null

  return (
    <>
      <ConversationActionButton
        primary={primary}
        text={title}
        size={size}
        inverse={inverse}
        theme={theme}
        onClick={openModal}
        testId="conversation-action--complete-transaction-button"
      />
      <ConfirmDeliveredEverythingIsOkModal
        isOpen={isModalOpen}
        onSubmit={confirmCompleteTransaction}
        onClose={closeModal}
      />
    </>
  )
}

export default CompleteDeliveredTransaction
